import { Time } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Parser } from 'expr-eval';
import { at, clone, set, transform } from 'lodash-es';
import reverse from 'lodash-es/reverse';
import sortBy from 'lodash-es/sortBy';
import * as moment from 'moment';

import { AppLocator } from '../../app.locator';

export class SpreengUtils {
	private static _parser: Parser;

	public static randomColor(): string {
		const letters = '0123456789ABCDEF';
		let color = '#';
		for (let i = 0; i < 6; i++) {
			color += letters[Math.floor(Math.random() * 16)];
		}
		return color;
	}

	public static timeToMinutes(time: Time) {
		return time.hours * 60 + time.minutes;
	}

	public static minutesToTime(offset: number): Time {
		return { hours: Math.floor(offset / 60), minutes: offset % 60 };
	}

	public static interpolate(value: string, params: any = {}): string {
		const translateService =
			AppLocator.inject<TranslateService>(TranslateService);

		const result: string = value
			? translateService.getParsedResult({ value }, 'value', params)
			: null;

		return result?.includes('{{') || result?.includes('}}') ? null : result;
	}

	public static get parser() {
		if (!SpreengUtils._parser) {
			SpreengUtils._parser = new Parser();
			SpreengUtils._parser.functions.at = (x, y) => {
				return at(x, y)[0];
			};
			SpreengUtils._parser.functions.exec = (x, y, z) => {
				const result = new RegExp(x, 'g').exec(y);
				return result ? result[z] : null;
			};
			SpreengUtils._parser.functions.includes = (x: any[], y) => {
				return x && x.includes(y);
			};
			SpreengUtils._parser.functions.isTrue = (x) => !!x;
			SpreengUtils._parser.functions.lower = (x: string) => x?.toLowerCase();
			SpreengUtils._parser.functions.startsWith = (x: string, y: string) =>
				x?.startsWith(y);
			SpreengUtils._parser.functions.set = (x, path, value) => set(x, path, value);
			SpreengUtils._parser.functions.isBlank = (x) => !x;
			SpreengUtils._parser.functions.isNotBlank = (x) => !!x;
			SpreengUtils._parser.functions.reverse = (x) => reverse(x);
			SpreengUtils._parser.functions.length = (x) => x.length || 0;
			SpreengUtils._parser.functions.sortBy = (x, fields) => sortBy(x, fields);
			SpreengUtils._parser.functions.reverse = (x) => reverse(clone(x));
			SpreengUtils._parser.functions.sortByRank = (
				x: { value: string }[],
				y: string,
				z: { [key: string]: any }
			) => {
				const values = x.map((i) => i.value);
				const counts = {};

				for (const value of values) {
					counts[value] = 0;
				}

				for (let i = 0; i < values.length; i++) {
					const questionName = values[i].replace(/^(.+)$/, y);

					for (const value of values.slice(i + 1)) {
						if (z[questionName].answer.value.includes(value)) {
							counts[value]++;
						} else {
							counts[values[i]]++;
						}
					}
				}

				return reverse(
					sortBy(
						transform(
							counts,
							(result, value, key) => result.push({ value: key, count: value }),
							[]
						),
						'count'
					)
				).map((i) => x.find((v) => v.value === i.value));
			};
			SpreengUtils._parser.functions.slice = (
				x: any[],
				start: number,
				end: number
			) => x.slice(start, end);
			SpreengUtils._parser.functions.dateDaysInMonth = (x) =>
				moment(x).daysInMonth();
			SpreengUtils._parser.functions.dateFormat = (x, l, format) =>
				moment(x).locale(l).format(format);
			SpreengUtils._parser.functions.dateEndOf = (x, unitOfTime) =>
				moment(x).endOf(unitOfTime);
			SpreengUtils._parser.functions.arrayOf = (length) =>
				new Array(length).fill(null);
			SpreengUtils._parser.functions.dateStartOf = (x, unitOfTime) =>
				moment(x).startOf(unitOfTime);
			SpreengUtils._parser.functions.dateAdd = (x, amount, unitOfTime) =>
				moment(x).add(amount, unitOfTime);
			SpreengUtils._parser.functions.dateDiff = (x, y, unitOfTime) =>
				moment(x).diff(y, unitOfTime);
		}

		return SpreengUtils._parser;
	}
}
